import {
    createDataActionFactory,
    ContactFoldersData,
    ContactFolder,
    createActionFactory,
    Feature,
    hashFolderId,
    sequentiallyProcessPromiseFactories,
} from '../../../shared';
import { getAppState, isPublicFoldersEnabled } from '../../../appstate';
import { default as getOwsContactFolders } from '../../dataLayer/ows/getContactFolders';
import { default as getOwsDeletedItemsFolders } from '../../dataLayer/ows/getDeletedItemsFolders';
import { getOwsFolders, GetFolderData } from '../../dataLayer/ows/getFolders';
import { subscribeToFolderNotifications } from './notificationActions';
import type { Dispatch } from 'redux';
import { getPersonasInFolder } from './personaActions';

export const contactFoldersLoadCompletedAction = createDataActionFactory<ContactFoldersData, Error>(
    'ContactFoldersLoadCompletedAction',
    Feature.FolderManagement,
    action => {
        const folderData = action.payload;
        return {
            defaultFolderId:
                folderData.defaultContactFolder && hashFolderId(folderData.defaultContactFolder.id),
            rootContactFolder:
                folderData.rootContactFolder && hashFolderId(folderData.rootContactFolder.id),
            displayedFolders: folderData.displayedFolders ? folderData.displayedFolders.length : 0,
        };
    }
);

export const deletedItemsFoldersLoadCompletedAction = createDataActionFactory<
    ContactFolder[],
    Error
>('DeletedItemsFoldersLoadCompletedAction', Feature.FolderManagement, action => {
    return {
        folders: action.payload.map(folder => hashFolderId(folder.id)).join(', '),
    };
});
export const getFoldersCompletedAction = createDataActionFactory<GetFolderData[], Error>(
    'GetFoldersCompletedAction',
    Feature.FolderManagement
);

export const contactFoldersSubscribedToNotificationsAction = createActionFactory(
    'ContactFoldersSubscribedToNotificationsAction',
    Feature.PeopleHubNotification
);
export const contactFoldersUnsubscribedToNotificationsAction = createActionFactory(
    'ContactFoldersUnsubscribedToNotificationsAction',
    Feature.PeopleHubNotification
);

const contactFoldersToIds = (contactFolders: ContactFoldersData) =>
    (contactFolders.defaultContactFolder ? [contactFolders.defaultContactFolder.id] : []).concat(
        contactFolders.displayedFolders.map(folder => folder.id)
    );

export const getContactFoldersAndSubscribeToChanges = (dispatch: Dispatch, getState: () => {}) =>
    getOwsContactFolders().then(
        contactFolders => {
            dispatch(contactFoldersLoadCompletedAction.success(contactFolders));
            dispatch(contactFoldersSubscribedToNotificationsAction());
            const folderIds = contactFoldersToIds(contactFolders);
            // Subscribe to changes in each folder
            folderIds.forEach(folderId => {
                subscribeToFolderNotifications(folderId, dispatch);
            });

            return contactFolders;
        },
        error => {
            dispatch(contactFoldersLoadCompletedAction.error(error));
        }
    );

export const getDeletedItemsFoldersAndSubscribeToChanges = (
    dispatch: Dispatch,
    getState: () => {}
) =>
    getOwsDeletedItemsFolders().then(
        deletedItemsFolders => {
            dispatch(deletedItemsFoldersLoadCompletedAction.success(deletedItemsFolders));
            return deletedItemsFolders;
        },
        error => {
            dispatch(deletedItemsFoldersLoadCompletedAction.error(error));
        }
    );

export const loadContentsOfAllContactFolders = (dispatch: Dispatch, getState: () => {}) => {
    const state = getAppState(getState());
    const contactFoldersState = state.data.contactFoldersState;
    const publicFolders = isPublicFoldersEnabled(state) ? contactFoldersState.publicFolders : [];

    return sequentiallyProcessPromiseFactories(
        [
            contactFoldersState.defaultContactFolder,
            ...contactFoldersState.displayedContactFolders,
            ...publicFolders,
        ]
            .filter(folder => !!folder)
            .map(folder => () => getPersonasInFolder(dispatch)(folder.id, true))
    );
};

export const loadEffectiveRightsForAllPublicFolders = (dispatch: Dispatch, getState: () => {}) => {
    const state = getAppState(getState());

    if (isPublicFoldersEnabled(state)) {
        const publicFolders = state.data.contactFoldersState.publicFolders;

        // If there are no public folders, no need to check access rights. Checking empty access
        // rights will mess up overall QOS numbers
        if (!publicFolders || publicFolders.length === 0) {
            return undefined;
        }

        const publicFolderMailbox =
            state.data.userConfiguration.settings.DefaultPublicFolderMailbox;
        return getFolders(dispatch)(
            [...publicFolders].map(folder => folder.id),
            publicFolderMailbox
        );
    } else {
        return undefined;
    }
};

export const getFolders = (dispatch: Dispatch) => (
    contactFolderIds: string[],
    publicFolderMailbox: string
) =>
    getOwsFolders(contactFolderIds, publicFolderMailbox).then(
        foldersData => {
            dispatch(getFoldersCompletedAction.success(foldersData));
        },
        error => {
            dispatch(contactFoldersLoadCompletedAction.error(error));
        }
    );

export const unsubscribeToChangesInAllContactFolders = (dispatch: Dispatch) => {
    dispatch(contactFoldersUnsubscribedToNotificationsAction());
};
export type { DataActionFactory } from '../../../shared';
export type { Action } from '../../../shared';
